<template>
  <step-template :subStep="subStep">
    
    <h4 class="pt-8 pb-sm-8 px-sm-8 text-center" v-html="question"></h4>
    <v-row v-if="bannerSrc">
      <v-col>
        <img
          :lazy-src="bannerSrc"
          contain
          :src="bannerSrc"
          style="margin: 0 auto; max-height: 200px;"
        />
      </v-col>
    </v-row>
    <!-- Single Select Mode (Radio Buttons) -->
    <v-radio-group v-if="!allowMultiple" v-model="selected">
      <v-alert
        v-for="(option, key) in options"
        :key="key"
        class="mx-md py-0 px-5"
        :border="borderPosition"
        colored-border
        :color="cColor(key)"
        elevation="2"
        v-ripple="{ center: true }"
      >
        <v-row class="cursor-pointer" @click="select(option)">
          <v-col cols="1" class="mt-1">
            <v-radio
              :value="option.value"
              :color="cColor(key)"
              :ripple="false"
              off-icon="mdi-checkbox-blank-circle-outline"
              on-icon="mdi-check-circle-outline"
            ></v-radio>
          </v-col>
          <v-col cols="11">
            <div v-if="option.title" class="text-h6" v-html="option.title"></div>
            <div v-if="option.content" v-html="option.content"></div>
          </v-col>
        </v-row>
      </v-alert>
    </v-radio-group>

    <!-- Multi Select Mode (Checkboxes) -->
    <div v-else>
      <v-alert
        v-for="(option, key) in options"
        :key="key"
        class="mx-md py-0 px-5"
        :border="borderPosition"
        colored-border
        :color="cColor(key)"
        elevation="2"
        v-ripple="{ center: true }"
      >
        <v-row class="cursor-pointer" @click="select(option)">
          <v-col cols="1" class="mt-1">
            <v-checkbox
              :input-value="isSelected(option.value)"
              :color="cColor(key)"
              :ripple="false"
              :disabled="!isSelected(option.value) && !canSelectMore"
              hide-details
              class="mt-0 pt-0"
              @click.stop="select(option)"
            ></v-checkbox>
          </v-col>
          <v-col cols="11">
            <div v-if="option.title" class="text-h6" v-html="option.title"></div>
            <div v-if="option.content" v-html="option.content"></div>
          </v-col>
        </v-row>
      </v-alert>
    </div>
  </step-template>
</template>

<script>
/**
 * To use:
 - in the database, go to table substeps
 - find the substep row that you want this component
 - update the row field component='step-multiple-choice'
 - update the row field component_props as a JSON like:

* SINGLE SELECT (default behavior - radio buttons)
{
  "variables": { "vmodel": "CUSTOM_VARIABLE_NAME" },
  "color": "orange",
  "colorAccent": false,
  "borderPosition": "left",
  "bannerSrc": "/img/banner.jpg",
  "question": "Who is the best power ranger?",
  "options": [
    { "value": "1", "title": "Red Ranger", "content": "Description..." },
    { "value": "2", "title": "Blue Ranger", "content": "Description..." }
  ]
}

* MULTI SELECT (checkboxes - user can select multiple options)
{
  "variables": { "vmodel": "selected_treatments" },
  "question": "Select all treatments that apply:",
  "allowMultiple": true,
  "options": [
    { "value": "1", "title": "Treatment A", "content": "Description..." },
    { "value": "2", "title": "Treatment B", "content": "Description..." },
    { "value": "3", "title": "Treatment C", "content": "Description..." }
  ]
}
// Data stored: { value: ["1", "2"], fullObject: [{...}, {...}] }
// Log format: "MultipleChoice Selected: selected_treatments: 1;2"

* MULTI SELECT WITH MIN/MAX CONSTRAINTS
{
  "variables": { "vmodel": "top_3_choices" },
  "question": "Select your top 3 choices (minimum 2, maximum 3):",
  "allowMultiple": true,
  "minSelections": 2,
  "maxSelections": 3,
  "options": [
    { "value": "A", "title": "Option A" },
    { "value": "B", "title": "Option B" },
    { "value": "C", "title": "Option C" },
    { "value": "D", "title": "Option D" },
    { "value": "E", "title": "Option E" }
  ]
}
// Continue button enabled only when 2-3 items selected
// Checkboxes disabled when max reached (can still deselect)

* PROPS:
// allowMultiple: boolean (default: false) - enables multi-select with checkboxes
// minSelections: number (default: 1) - minimum selections required for continue button
// maxSelections: number (default: null/unlimited) - maximum selections allowed
 */
import { mapState, mapMutations, mapActions } from "vuex";
import StepTemplate from "/src/components/steps/StepTemplate.vue";

export default {
  name: "StepMultipleChoice",
  components: {
    StepTemplate,
  },
  props: {
    steps: {
      type: Array,
      required: true,
    },
    step: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    subStep: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    question: {
      type: String,
      required: true,
      default: "...",
    },
    options: {
      type: Array,
      required: false,
      default: () => {
        return [];
      },
    },
    variables: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    color: {
      type: String,
      required: false,
      default: "orange",
    },
    colorAccent: {
      type: Boolean,
      required: false,
      default: true,
    },
    borderPosition: {
      type: String,
      required: false,
      default: "left",
    },
    bannerSrc: {
      type: String,
      default: "",
      required: false,
    },
    allowMultiple: {
      type: Boolean,
      default: false,
      required: false,
    },
    minSelections: {
      type: Number,
      default: 1,
      required: false,
    },
    maxSelections: {
      type: Number,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      selected: null,
      selectedMultiple: [],
    };
  },
  computed: {
    ...mapState({
      caseData: (state) => state.caseData,
    }),
    cColor() {
      return (key) => {
        let accent;
        if (this.colorAccent) accent = `accent-${key + 1}`;
        return `${this.color} ${accent}`;
      };
    },
    isSelected() {
      return (optionValue) => {
        if (this.allowMultiple) {
          return this.selectedMultiple.includes(optionValue);
        }
        return this.selected === optionValue;
      };
    },
    selectionCount() {
      if (this.allowMultiple) {
        return this.selectedMultiple.length;
      }
      return this.selected ? 1 : 0;
    },
    meetsMinimum() {
      return this.selectionCount >= this.minSelections;
    },
    meetsMaximum() {
      if (this.maxSelections === null) return true;
      return this.selectionCount <= this.maxSelections;
    },
    canSelectMore() {
      if (!this.allowMultiple) return true;
      if (this.maxSelections === null) return true;
      return this.selectionCount < this.maxSelections;
    },
    isValidSelection() {
      return this.meetsMinimum && this.meetsMaximum;
    },
  },
  watch: {
    selected(val) {
      if (!this.allowMultiple) {
        this.setContinueButtonEnabled(!!val);
      }
    },
    selectedMultiple: {
      handler() {
        if (this.allowMultiple) {
          this.setContinueButtonEnabled(this.isValidSelection);
        }
      },
      deep: true,
    },
  },
  created() {
    this.init();
  },
  methods: {
    ...mapMutations(["setContinueButtonEnabled", "setForceNextSubStepTo"]),
    ...mapActions(["setCaseDataValue", "trackingAction"]),
    init() {
      this.loadPreviousSelectedChoice();
      this.setContinueButtonEnabled(false);
    },
    select(option) {
      if (this.allowMultiple) {
        this.selectMultiple(option);
      } else {
        this.selectSingle(option);
      }
    },
    selectSingle(option) {
      this.setCaseDataValue({
        key: this.variables.vmodel,
        value: option.value,
        fullObject: option,
      });
      this.selected = option.value;
      this.setContinueButtonEnabled(true);
      this.trackingAction(`MultipleChoice Selected: ${this.variables.vmodel}: ${option.value}`);
    },
    selectMultiple(option) {
      const index = this.selectedMultiple.indexOf(option.value);
      if (index > -1) {
        // Deselect: remove from array
        this.selectedMultiple.splice(index, 1);
      } else {
        // Select: add to array if under max
        if (this.canSelectMore) {
          this.selectedMultiple.push(option.value);
        }
      }
      // Get full objects for all selected values
      const selectedObjects = this.options.filter((opt) =>
        this.selectedMultiple.includes(opt.value)
      );
      // Store array of values and array of full objects
      this.setCaseDataValue({
        key: this.variables.vmodel,
        value: [...this.selectedMultiple],
        fullObject: selectedObjects,
      });
      // Log with semicolon-delimited values
      const logValues = this.selectedMultiple.join(";");
      this.trackingAction(`MultipleChoice Selected: ${this.variables.vmodel}: ${logValues}`);
    },
    loadPreviousSelectedChoice() {
      const currentValue = this.caseData[this.variables.vmodel];
      if (!currentValue) return;

      if (this.allowMultiple) {
        // Handle array of values
        if (Array.isArray(currentValue)) {
          this.selectedMultiple = [...currentValue];
        }
      } else {
        // Handle single value
        const currentOption = this.options.find((option) => {
          if (option.value == currentValue) {
            return true;
          }
        });
        if (currentOption) {
          this.selected = currentOption.value;
        }
      }
    },
  },
};
</script>
